/**
 * CSV Export Utility
 * 
 * Provides functions to export data to CSV format with French locale formatting:
 * - Semicolon (;) as delimiter
 * - Comma (,) as decimal separator
 * - DD/MM/YYYY date format
 * - UTF-8 BOM for Excel compatibility
 */

/**
 * Format a number for CSV export (French locale)
 * Uses comma as decimal separator
 */
export function formatNumberForCSV(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '0';
  }
  
  // Check if it's a whole number
  if (Number.isInteger(value)) {
    return value.toString();
  }
  
  // Format with comma as decimal separator
  return value.toString().replace('.', ',');
}

/**
 * Format a date for CSV export (DD/MM/YYYY format)
 */
export function formatDateForCSV(date: string | Date | null | undefined): string {
  if (!date) {
    return '';
  }
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
}

/**
 * Escape CSV value (handles special characters, quotes, semicolons)
 */
function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  const str = String(value);
  
  // If value contains semicolon, quote, or newline, wrap in quotes and escape quotes
  if (str.includes(';') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
}

/**
 * Convert data array to CSV string
 */
function convertToCSV(data: Record<string, any>[], headers: string[]): string {
  if (data.length === 0) {
    return headers.join(';');
  }
  
  // Build CSV rows
  const rows: string[] = [];
  
  // Add header row
  rows.push(headers.map(escapeCSVValue).join(';'));
  
  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }
      
      return escapeCSVValue(value);
    });
    
    rows.push(values.join(';'));
  }
  
  return rows.join('\n');
}

/**
 * Export data to CSV file with French formatting
 * 
 * @param data Array of objects to export
 * @param filename Name of the file (without extension)
 * @param headers Optional array of header names (if not provided, will be auto-detected from data keys)
 */
export function exportToCSV(
  data: Record<string, any>[],
  filename: string,
  headers?: string[]
): void {
  // Auto-detect headers from data if not provided
  let finalHeaders: string[];
  if (headers && headers.length > 0) {
    finalHeaders = headers;
  } else if (data.length > 0) {
    // Get all unique keys from all objects
    const allKeys = new Set<string>();
    data.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
    finalHeaders = Array.from(allKeys);
  } else {
    finalHeaders = [];
  }
  
  // Convert data to CSV format
  const csvContent = convertToCSV(data, finalHeaders);
  
  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csvContent;
  
  // Create blob
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Remove duplicate ID / label columns after merging aggregate article rows with French details.
 */
function dedupeMergedArticleExportRow(row: Record<string, any>): void {
  const canonId =
    row.article_id !== undefined && row.article_id !== null && row.article_id !== ''
      ? String(row.article_id)
      : '';

  const refStr = row.ref !== undefined && row.ref !== null && row.ref !== '' ? String(row.ref) : '';

  if (canonId && refStr && canonId === refStr) {
    delete row.ref;
  }

  const articleName =
    typeof row.article_name === 'string' ? row.article_name.trim() : '';
  const nom = typeof row.nom === 'string' ? row.nom.trim() : '';

  if (articleName && nom && articleName === nom) {
    delete row.article_name;
  }
}

/**
 * Format articles for export
 * Combines article metadata with article details
 * 
 * @param articles Array of article metadata objects
 * @param articleDetails Array of article detail objects
 * @returns Formatted array ready for CSV export
 */
export function formatArticleExport(
  articles: any[],
  articleDetails: any[]
): Record<string, any>[] {
  // Create a map of article details by ref
  const detailsMap = new Map<string, any>();
  articleDetails.forEach((detail: any) => {
    if (detail?.ref) {
      detailsMap.set(String(detail.ref), detail);
    }
  });
  
  // Combine articles with their details
  return articles.map((article: any) => {
    const articleId = String(article?.article_id || article?.ref || '');
    const detail = detailsMap.get(articleId) || {};
    
    const row: Record<string, any> = {
      ...article,
      ...detail,
      // Ensure ref is present for lookups; may be stripped in dedupe if redundant with article_id
      ref: articleId || (detail.ref != null ? String(detail.ref) : ''),
    };

    dedupeMergedArticleExportRow(row);
    return row;
  });
}

/**
 * Pivot sales data to CSV format
 * Creates a pivot table where rows are articles and columns are dates
 * 
 * @param data Array of sales data objects
 * @param rowKey Key for row identifier (e.g., 'article', 'ref')
 * @param colKey Key for column identifier (e.g., 'date')
 * @param valueKey Key for value (e.g., 'sales', 'quantity')
 * @returns CSV string with pivot format
 */
export function pivotSalesToCSV(
  data: any[],
  rowKey: string,
  colKey: string,
  valueKey: string
): string {
  if (!data || data.length === 0) {
    return '';
  }
  
  // Collect all unique row identifiers and column identifiers
  const rowIds = new Set<string>();
  const colIds = new Set<string>();
  
  data.forEach((item: any) => {
    const rowId = String(item[rowKey] || '');
    const colId = String(item[colKey] || '');
    if (rowId) rowIds.add(rowId);
    if (colId) colIds.add(colId);
  });
  
  const sortedRowIds = Array.from(rowIds).sort();
  const sortedColIds = Array.from(colIds).sort();
  
  // Create a map for quick lookup: rowId -> colId -> value
  const pivotMap = new Map<string, Map<string, number>>();
  
  data.forEach((item: any) => {
    const rowId = String(item[rowKey] || '');
    const colId = String(item[colKey] || '');
    const value = Number(item[valueKey] || 0);
    
    if (!pivotMap.has(rowId)) {
      pivotMap.set(rowId, new Map());
    }
    pivotMap.get(rowId)!.set(colId, value);
  });
  
  // Build CSV rows
  const rows: string[] = [];
  
  // Header row
  const headerRow = [rowKey, ...sortedColIds];
  rows.push(headerRow.map(escapeCSVValue).join(';'));
  
  // Data rows
  sortedRowIds.forEach(rowId => {
    const row = [rowId];
    sortedColIds.forEach(colId => {
      const value = pivotMap.get(rowId)?.get(colId) || 0;
      row.push(String(value));
    });
    rows.push(row.map(escapeCSVValue).join(';'));
  });
  
  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  return BOM + rows.join('\n');
}

/**
 * Export pivoted table to CSV format (with base month support)
 * Similar to pivotSalesToCSV but supports base month for column ordering
 * 
 * @param data Array of data objects
 * @param rowKey Key for row identifier
 * @param colKey Key for column identifier (typically month index)
 * @param valueKey Key for value
 * @param baseMonth Optional base month (YYYY-MM format) for column ordering
 * @returns CSV string with pivot format
 */
export function exportPivotedTableToCSV(
  data: any[],
  rowKey: string,
  colKey: string,
  valueKey: string,
  baseMonth?: string
): string {
  if (!data || data.length === 0) {
    return '';
  }
  
  // Collect all unique row identifiers and column identifiers
  const rowIds = new Set<string>();
  const colIds = new Set<string>();
  
  data.forEach((item: any) => {
    const rowId = String(item[rowKey] || '');
    const colId = String(item[colKey] || '');
    if (rowId) rowIds.add(rowId);
    if (colId) colIds.add(colId);
  });
  
  const sortedRowIds = Array.from(rowIds).sort();
  
  // Sort columns - if baseMonth provided, order relative to it, otherwise sort normally
  let sortedColIds: string[];
  if (baseMonth) {
    // Sort columns (baseMonth can be used for future enhancements)
    sortedColIds = Array.from(colIds).sort((a, b) => {
      // Try to parse as month index (number) or date string
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      // Fallback to string comparison
      return a.localeCompare(b);
    });
  } else {
    sortedColIds = Array.from(colIds).sort();
  }
  
  // Create a map for quick lookup: rowId -> colId -> value
  const pivotMap = new Map<string, Map<string, number | null>>();
  
  data.forEach((item: any) => {
    const rowId = String(item[rowKey] || '');
    const colId = String(item[colKey] || '');
    const value = item[valueKey] !== null && item[valueKey] !== undefined 
      ? Number(item[valueKey]) 
      : null;
    
    if (!pivotMap.has(rowId)) {
      pivotMap.set(rowId, new Map());
    }
    pivotMap.get(rowId)!.set(colId, value);
  });
  
  // Build CSV rows
  const rows: string[] = [];
  
  // Header row
  const headerRow = [rowKey, ...sortedColIds];
  rows.push(headerRow.map(escapeCSVValue).join(';'));
  
  // Data rows
  sortedRowIds.forEach(rowId => {
    const row = [rowId];
    sortedColIds.forEach(colId => {
      const value = pivotMap.get(rowId)?.get(colId);
      if (value === null || value === undefined) {
        row.push('');
      } else {
        row.push(String(value));
      }
    });
    rows.push(row.map(escapeCSVValue).join(';'));
  });
  
  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  return BOM + rows.join('\n');
}

/**
 * Download CSV content with BOM
 * 
 * @param csvContent CSV content string
 * @param filename Filename for download
 */
export function downloadCSVWithBOM(csvContent: string, filename: string): void {
  // Ensure BOM is present
  const BOM = '\uFEFF';
  const csvWithBOM = csvContent.startsWith(BOM) ? csvContent : BOM + csvContent;
  
  // Create blob
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export outstock declarations to CSV format
 * 
 * @param declarations Array of outstock declaration objects
 * @returns CSV content string
 */
export function exportOutstockDeclarationsToCSV(declarations: any[]): string {
  if (!declarations || declarations.length === 0) {
    return '';
  }
  
  // Define headers
  const headers = [
    'ID',
    'Article ID',
    'Date début',
    'Date fin',
    'Substituts',
    'Créé le',
    'Mis à jour le'
  ];
  
  // Build CSV rows
  const rows: string[] = [];
  rows.push(headers.map(escapeCSVValue).join(';'));
  
  declarations.forEach((declaration: any) => {
    const row = [
      String(declaration.id || ''),
      String(declaration.product_id || ''),
      formatDateForCSV(declaration.start_date),
      formatDateForCSV(declaration.end_date),
      declaration.substitutes_articles 
        ? declaration.substitutes_articles.map(([id]: [any, any]) => id).join(', ')
        : '',
      formatDateForCSV(declaration.created_at),
      formatDateForCSV(declaration.updated_at)
    ];
    rows.push(row.map(escapeCSVValue).join(';'));
  });
  
  return rows.join('\n');
}
